import styled from 'styled-components';

import { RESULTS_FILTER_STRIP_HEIGHT } from '../../components/SearchBar/styles';
import {
  isSearchResultsListPath,
  isVendorDetailPath,
  shouldShowSearchBar,
  shouldShowCategoryNav,
} from '../../siteConfigs/serviceCategories';

// Fallback values for initial render; AppContent measures the real header height.
export const HEADER_NAV_HEIGHT = 152;
export const MOBILE_HEADER_NAV_HEIGHT = 168;
export const COMPACT_HEADER_NAV_HEIGHT = 56;
export const MOBILE_COMPACT_HEADER_NAV_HEIGHT = 56;
export const SEARCH_BAR_HEIGHT = 88;
export const MOBILE_LANDING_SEARCH_BAR_HEIGHT = 92;
export const COMPACT_SEARCH_BAR_HEIGHT = 58;
export const MOBILE_COMPACT_SEARCH_BAR_HEIGHT = 56;

/** Matches `.primary-clr` / `.primary-btn`. Logo mark is `#ff5a5f` (icon2.svg). */
const BRAND_PINK = '#FF385C';

const mainBarDesktopHeight = props => {
  if (props.$compactSearch) return '56px';
  if (props.$withCenterCategoryNav) return 'auto';
  return '88px';
};

const mainBarDesktopMinHeight = props => {
  if (props.$compactSearch) return '56px';
  if (props.$withCenterCategoryNav) return '128px';
  return '88px';
};

const isMobileViewport = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(max-width: 768px)').matches;

export const getHeaderOffset = (pathname, isLoggedIn = false) => {
  if (isVendorDetailPath(pathname)) {
    return 0;
  }

  const mobile = isMobileViewport();
  const compactResultsOrDetail =
    isSearchResultsListPath(pathname) || isVendorDetailPath(pathname);

  let navHeight = COMPACT_HEADER_NAV_HEIGHT;
  if (!compactResultsOrDetail && shouldShowCategoryNav(pathname, isLoggedIn)) {
    navHeight = mobile ? MOBILE_HEADER_NAV_HEIGHT : HEADER_NAV_HEIGHT;
  } else if (compactResultsOrDetail && mobile) {
    navHeight = MOBILE_COMPACT_HEADER_NAV_HEIGHT;
  }

  if (shouldShowSearchBar(pathname)) {
    if (isSearchResultsListPath(pathname)) {
      return (
        navHeight +
        (mobile
          ? MOBILE_COMPACT_SEARCH_BAR_HEIGHT
          : COMPACT_SEARCH_BAR_HEIGHT) +
        RESULTS_FILTER_STRIP_HEIGHT
      );
    }
    if (isVendorDetailPath(pathname)) {
      return (
        navHeight +
        (mobile ? MOBILE_COMPACT_SEARCH_BAR_HEIGHT : COMPACT_SEARCH_BAR_HEIGHT)
      );
    }
    return (
      navHeight +
      (mobile ? MOBILE_LANDING_SEARCH_BAR_HEIGHT : SEARCH_BAR_HEIGHT) +
      10
    );
  }

  return navHeight;
};

export const TopNav = styled.section`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-bottom: 1px solid #ebebeb;
  position: relative;
  z-index: ${props => (props.$menuOpen ? 1102 : 1)};

  @media (max-width: 768px) {
    overflow-x: visible;
    overflow-y: visible;
  }
`;

export const MainBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: ${props => (props.$compactSearch ? '56px' : '88px')};
  position: relative;

  @media (min-width: 901px) {
    height: ${mainBarDesktopHeight};
    min-height: ${mainBarDesktopMinHeight};
  }

  @media (max-width: 768px) {
    padding: 0 12px;
    height: ${props => (props.$compactSearch ? '56px' : '72px')};
  }
`;

export const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 2;

  .brand-logo > a > img {
    height: 32px;
  }
`;

export const CategoryNav = styled.nav`
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  display: flex;
  align-items: flex-end;
  gap: 4px;
  z-index: 1;
  pointer-events: auto;

  @media (max-width: 900px) {
    display: none;
  }
`;

export const CategoryNavMobile = styled.nav`
  display: none;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-x: auto;
  overflow-y: hidden;
  padding: ${props => (props.$compactSearch ? '4px 16px 0' : '12px 16px 0')};
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  scroll-snap-type: x proximity;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 900px) {
    display: flex;
    justify-content: center;
  }

  @media (max-width: 768px) {
    padding: ${props => (props.$compactSearch ? '2px 12px 0' : '8px 12px 0')};
  }
`;

export const CategoryNavMobileTrack = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: nowrap;
  flex-shrink: 0;
  gap: 4px;
  margin: 0 auto;

  @media (max-width: 768px) {
    gap: 2px;
  }
`;

export const CategoryTab = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 18px 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-bottom: 2px solid ${props => (props.$active ? '#222' : 'transparent')};
  transition:
    color 0.2s ease,
    transform 0.2s ease,
    opacity 0.2s ease;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-1px);

    .tab-label {
      color: #222;
    }

    .category-icon {
      opacity: 1;
    }
  }

  .category-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    opacity: ${props => (props.$active ? 1 : 0.88)};
    transition: opacity 0.2s ease;

    svg,
    img {
      width: 44px;
      height: 44px;
      object-fit: contain;
    }
  }

  .tab-label {
    font-size: 12px;
    font-weight: ${props => (props.$active ? 500 : 400)};
    color: ${props => (props.$active ? '#222' : '#484848')};
    white-space: nowrap;
    transition: color 0.2s ease;
  }

  @media (max-width: 900px) {
    .category-icon svg,
    .category-icon img {
      width: 40px;
      height: 40px;
    }

    .tab-label {
      font-size: 11px;
    }
  }

  @media (max-width: 768px) {
    padding: 8px 10px 6px;
    gap: 4px;
    scroll-snap-align: center;

    .category-icon svg,
    .category-icon img {
      width: 32px;
      height: 32px;
    }

    .tab-label {
      font-size: 10px;
      line-height: 1.15;
      text-align: center;
      max-width: 76px;
      white-space: normal;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
`;

export const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 2;

  @media (max-width: 768px) {
    gap: 4px;
  }
`;

export const BusinessLink = styled.a`
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #222;
  border-radius: 22px;
  white-space: nowrap;
  transition:
    background 0.2s ease,
    color 0.2s ease;

  &:hover {
    background: #f7f7f7;
    color: #000;
  }

  @media (max-width: 768px) {
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 400;
  }

  @media (max-width: 576px) {
    display: none;
  }
`;

export const UserSignButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1.5px solid #717171;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  transition:
    box-shadow 0.2s ease,
    background 0.2s ease;
  padding: 0;
  overflow: hidden;
  flex-shrink: 0;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    background: #f7f7f7;
  }

  .anticon {
    font-size: 16px;
    color: #484848;
  }

  &.is-logged-out {
    border-color: #222;

    &:hover {
      border-color: ${BRAND_PINK};

      .anticon {
        color: ${BRAND_PINK};
      }
    }
  }

  &.is-logged-in {
    border: 1.5px solid ${BRAND_PINK};
    padding: 0;
    background: #fff;

    &:hover {
      box-shadow: 0 2px 10px rgba(230, 30, 77, 0.35);
      border-color: ${BRAND_PINK};
    }

    .ant-avatar {
      width: 32px;
      height: 32px;
      line-height: 32px;
      font-size: 13px;
      font-weight: 600;
      background: ${BRAND_PINK};
      color: #fff;
      border: none;
    }

    .ant-avatar-image {
      width: 100%;
      height: 100%;
    }
  }
`;

export const MobileMenuToggle = styled.button`
  display: none;
  cursor: pointer;
  padding: 8px;
  border: none;
  background: transparent;
  color: #222222;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
  }
`;

const HeaderWrapper = styled.div`
  position: ${props => (props.$nonSticky ? 'relative' : 'fixed')};
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  background: #fff;
  z-index: 1100;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  isolation: isolate;

  .overlay {
    height: 100%;
    position: fixed;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 1101;
    background: rgba(0, 0, 0, 0.25);
  }

  .mobile-drawer {
    display: none;
  }

  @media (max-width: 768px) {
    .mobile-drawer {
      display: ${props => (props.$menuOpen ? 'block' : 'none')};
      position: fixed;
      top: ${props => (props.$compactSearch ? '56px' : '72px')};
      left: 0;
      right: 0;
      background: #fff;
      border-bottom: 1px solid #ebebeb;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
      padding: 8px 0;
      z-index: 1103;

      a,
      button {
        position: relative;
        z-index: 1;
        display: block;
        width: 100%;
        padding: 12px 24px;
        text-align: left;
        border: none;
        background: none;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.4;
        color: #222;
        cursor: pointer;

        &:hover {
          background: #f7f7f7;
        }
      }
    }
  }
`;

export default HeaderWrapper;
